import React, { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import KrgButton, {
  BACKGROUND_ENUM,
  THEME_ENUM as BUTTON_THEME_ENUM,
  VARIANT_ENUM as BUTTON_VARIANT_ENUM,
} from "@kargo/shared-components.krg-button";
import { Video } from "../../../../api/types";
import CustomPlaylistList from "./CustomPlaylistList";
import PlaylistDataTable from "./PlaylistDataTable";
import EmptyPlaylistState from "../../../PlaylistsTable/EmptyPlaylistState";
import AddVideosModal from "../../AddVideosModal";
import { sortByDate } from "../../../../utils/sortingUtils";

interface VideoManagementProps {
  selectedVideos: Video[];
  setSelectedVideos: (videos: Video[]) => void;
  sortValue: string;
  publisherSlug?: string;
  asAdmin?: boolean;
  isAddVideosModalOpen: boolean;
  setIsAddVideosModalOpen: (open: boolean) => void;
  onSortedVideosChange?: (sortedVideos: Video[]) => void;
}

const VideoManagement: React.FC<VideoManagementProps> = ({
  selectedVideos,
  setSelectedVideos,
  sortValue,
  publisherSlug,
  asAdmin,
  isAddVideosModalOpen,
  setIsAddVideosModalOpen,
  onSortedVideosChange,
}) => {
  // Sort videos based on selected sort type
  const sortedVideos = useMemo(() => {
    if (!sortValue || sortValue === "manual") {
      return selectedVideos;
    }
    return sortByDate(selectedVideos, sortValue);
  }, [selectedVideos, sortValue]);

  // Notify parent component of sorted videos for API calls
  useEffect(() => {
    if (onSortedVideosChange) {
      onSortedVideosChange(sortedVideos);
    }
  }, [sortedVideos, onSortedVideosChange]);

  // Handle reordering videos (only for manual sort)
  const handleReorder = (oldIndex: number, newIndex: number) => {
    const newVideos = [...selectedVideos];
    const [movedVideo] = newVideos.splice(oldIndex, 1);
    newVideos.splice(newIndex, 0, movedVideo);
    setSelectedVideos(newVideos);
  };

  // Handle removing a video
  const handleRemoveVideo = (videoToken: string) => {
    setSelectedVideos(selectedVideos.filter((v) => v.token !== videoToken));
  };

  const handleAddVideos = (videos: Video[]) => {
    const newSelectedVideos = [...selectedVideos, ...videos];
    setSelectedVideos(newSelectedVideos);
    setIsAddVideosModalOpen(false);
  };

  return (
    <Box>
      <Box sx={{ marginBottom: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Choose existing videos from your library to add to your playlist.
        </Typography>
      </Box>

      <Box sx={{ marginBottom: 2 }}>
        <KrgButton
          theme={BUTTON_THEME_ENUM.v2}
          variant={BUTTON_VARIANT_ENUM.outlined}
          startIcon={<Add />}
          style={{
            borderColor: BACKGROUND_ENUM.blue,
            color: BACKGROUND_ENUM.blue,
          }}
          text="ADD VIDEOS"
          onClick={() => setIsAddVideosModalOpen(true)}
        />
      </Box>

      <Box sx={{ marginTop: 2, width: "852px" }}>
        {(() => {
          if (sortedVideos.length === 0) {
            return <EmptyPlaylistState />;
          }

          if (sortValue === "manual") {
            return (
              <CustomPlaylistList
                videos={sortedVideos}
                onReorder={handleReorder}
                onRemove={handleRemoveVideo}
              />
            );
          }

          return (
            <PlaylistDataTable
              videos={sortedVideos}
              onRemove={handleRemoveVideo}
            />
          );
        })()}
      </Box>

      <Box
        sx={{
          display: "flex",
          position: "absolute",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 2,
          paddingTop: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Total Videos: {sortedVideos.length}
        </Typography>
      </Box>

      {/* Add Videos Modal */}
      <AddVideosModal
        isOpen={isAddVideosModalOpen}
        onClose={() => setIsAddVideosModalOpen(false)}
        onAddVideos={handleAddVideos}
        publisherSlug={publisherSlug}
        asAdmin={asAdmin}
        currentSelectedVideos={selectedVideos}
      />
    </Box>
  );
};

export default VideoManagement;
